import { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Checkbox } from "@/components/ui/form-controls";
import { Field, Input } from "@/components/ui/input";
import { Modal } from "@/components/ui/modal";
import { Select } from "@/components/ui/select";
import { verifiedDomains, type SsoDomainsState } from "@/features/admin/sso/use-sso-domains";
import { formatTemplate, type SsoLocaleCatalog } from "@/i18n/use-sso-locale";
import { refusalMessage, useClipboard } from "@/features/admin/sso/sso-shared";
import {
  SSO_PROVIDER_TEMPLATES,
  createSsoOidcProvider,
  withStepUpRetry,
  type ApiSsoProvider,
  type SsoProviderTemplate,
} from "@/lib/api";

/**
 * Admin → Single sign-on → "Add OIDC identity provider" dialog (ALL-5464,
 * page-spec action `admin_adds_oidc_provider`).
 *
 * Calls exactly one operation: `createOidcProvider`
 * (POST /v1/admin/sso/providers/oidc). The body carries only the six fields
 * `OidcProviderCreateBody` declares — `additionalProperties: false` — and the
 * client secret is write-only, so it is dropped from component state the moment
 * the API accepts it and is never echoed back to the operator.
 *
 * The domain picker offers only the organization's VERIFIED challenges: the API
 * refuses anything else with SSO_DOMAIN_NOT_VERIFIED, so offering an unverified
 * domain would offer a guaranteed failure. The redirect URI and bookmark URL
 * come from the 201 response, never from a client-side guess.
 */
export function SsoProviderForm({
  open,
  onOpenChange,
  catalog,
  domainsState,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: SsoLocaleCatalog;
  domainsState: SsoDomainsState;
  onCreated: () => void;
}) {
  const ui = catalog.ui;
  const [displayName, setDisplayName] = useState("");
  const [template, setTemplate] = useState<SsoProviderTemplate | "">("");
  const [issuer, setIssuer] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ message: string; supportReference?: string; capability?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<ApiSsoProvider | null>(null);
  const { copied, copy } = useClipboard();

  const options = verifiedDomains(domainsState);

  // Every open starts from a clean form: a half-filled provider from a previous
  // attempt must never look like it is already saved.
  useEffect(() => {
    if (!open) return;
    setDisplayName("");
    setTemplate("");
    setIssuer("");
    setClientId("");
    setClientSecret("");
    setSelectedDomains([]);
    setError(null);
    setNotice(null);
    setCreated(null);
  }, [open]);

  function toggleDomain(domain: string, checked: boolean) {
    setSelectedDomains((current) => (
      checked ? [...current, domain] : current.filter((item) => item !== domain)
    ));
  }

  async function handleSubmit(event?: React.FormEvent) {
    event?.preventDefault();
    if (!displayName.trim() || !template || !issuer.trim() || !clientId.trim() || !clientSecret) {
      setError(ui["sso.form.required"]);
      return;
    }
    if (selectedDomains.length === 0) {
      setError(ui["sso.form.domainsRequired"]);
      return;
    }
    setError(null);
    setNotice(null);
    setBusy(true);
    const outcome = await withStepUpRetry(() => createSsoOidcProvider({
      displayName: displayName.trim(),
      template: template as SsoProviderTemplate,
      issuer: issuer.trim(),
      clientId: clientId.trim(),
      clientSecret,
      domains: selectedDomains,
    }));
    setBusy(false);

    // The secret is not kept a moment longer than the request needed it.
    setClientSecret("");

    if (outcome.stepUpFailed) {
      setNotice({
        message: ui["sso.shared.stepUpDescription"],
        supportReference: outcome.stepUpSupportReference,
      });
      return;
    }
    const result = outcome.result;
    if (!result.ok) {
      setNotice({
        message: refusalMessage(ui, result.reasonCode) ?? ui["sso.refusal.AUTH_POLICY_UNAVAILABLE"],
        supportReference: result.supportReference,
        capability: result.capability,
      });
      return;
    }
    if (!result.provider?.identityProviderId) {
      // A 2xx without a ProviderDetail is not something this dialog can render.
      setNotice({ message: ui["sso.shared.deniedDescription"], supportReference: result.supportReference });
      return;
    }
    setCreated(result.provider);
    onCreated();
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!busy) onOpenChange(next);
      }}
      title={ui["sso.form.title"]}
      description={ui["sso.form.description"]}
      size="md"
      footer={
        created ? (
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {ui["sso.shared.cancel"]}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
              {ui["sso.shared.cancel"]}
            </Button>
            <Button loading={busy} onClick={() => void handleSubmit()}>
              {busy ? ui["sso.form.busy"] : ui["sso.form.submit"]}
            </Button>
          </>
        )
      }
    >
      {notice && (
        <div data-testid="sso-provider-form-notice">
          <InfoBanner variant="danger">
            {notice.message}
            <SupportDetails reference={notice.supportReference} capability={notice.capability} />
          </InfoBanner>
        </div>
      )}

      {created ? (
        <div data-testid="sso-provider-created">
          <p className="text-xs font-semibold text-ink">{ui["sso.form.createdTitle"]}</p>
          <p className="mt-1 text-xs text-muted">{ui["sso.form.createdDescription"]}</p>
          <dl className="mt-3 flex flex-col gap-3 rounded-md border border-line bg-bg px-3 py-3">
            {([
              { key: "redirect", labelKey: "sso.form.redirectUri", value: created.redirectUri },
              { key: "bookmark", labelKey: "sso.form.bookmarkUrl", value: created.bookmarkUrl },
            ] as const).map((row) => (
              <div key={row.key} className="flex flex-col gap-0.5">
                <dt className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted">
                  {ui[row.labelKey]}
                </dt>
                <dd className="flex flex-wrap items-center gap-2">
                  <code className="break-all font-mono text-[11.5px] text-ink">{row.value}</code>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="min-h-[36px]"
                    aria-label={formatTemplate(ui["sso.form.copyValue"], { label: ui[row.labelKey] })}
                    onClick={() => void copy(row.key, row.value)}
                  >
                    <Copy aria-hidden className="size-3.5" />
                    {copied === row.key ? ui["sso.shared.copied"] : ui["sso.shared.copy"]}
                  </Button>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <form id="sso-oidc-provider-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Field label={ui["sso.form.displayName"]} htmlFor="sso-provider-display-name" required>
            <Input
              id="sso-provider-display-name"
              name="displayName"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder={ui["sso.form.displayNamePlaceholder"]}
              autoComplete="off"
              disabled={busy}
            />
          </Field>

          <Field label={ui["sso.form.template"]} htmlFor="sso-provider-template" required>
            <Select
              id="sso-provider-template"
              name="template"
              className="w-full"
              value={template}
              onChange={(event) => setTemplate(event.target.value as SsoProviderTemplate | "")}
              disabled={busy}
            >
              <option value="">{ui["sso.form.templatePlaceholder"]}</option>
              {SSO_PROVIDER_TEMPLATES.map((value) => (
                <option key={value} value={value}>
                  {ui[`sso.form.template.${value}`]}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label={ui["sso.form.issuer"]}
            htmlFor="sso-provider-issuer"
            hint={ui["sso.form.issuerHint"]}
            required
          >
            <Input
              id="sso-provider-issuer"
              name="issuer"
              value={issuer}
              onChange={(event) => setIssuer(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
          </Field>

          <Field label={ui["sso.form.clientId"]} htmlFor="sso-provider-client-id" required>
            <Input
              id="sso-provider-client-id"
              name="clientId"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
          </Field>

          <Field
            label={ui["sso.form.clientSecret"]}
            htmlFor="sso-provider-client-secret"
            hint={ui["sso.form.clientSecretHint"]}
            required
          >
            <Input
              id="sso-provider-client-secret"
              name="clientSecret"
              type="password"
              value={clientSecret}
              onChange={(event) => setClientSecret(event.target.value)}
              autoComplete="new-password"
              spellCheck={false}
              disabled={busy}
            />
          </Field>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs font-semibold text-ink">{ui["sso.form.domains"]}</legend>
            <p className="text-xs text-muted">{ui["sso.form.domainsHint"]}</p>
            {options.length === 0 ? (
              <p className="rounded-md border border-line bg-bg px-3 py-2 text-xs text-muted">
                {ui["sso.form.domainsNone"]}
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {options.map((domain) => (
                  <div key={domain} className="flex items-center gap-2">
                    <Checkbox
                      ariaLabel={domain}
                      checked={selectedDomains.includes(domain)}
                      onCheckedChange={(checked) => toggleDomain(domain, checked)}
                      disabled={busy}
                    />
                    {/* A verified domain is an unbreakable-looking monospace
                        string; `min-w-0 break-all` keeps a long one inside the
                        dialog instead of widening the checkbox row. */}
                    <span className="min-w-0 break-all font-mono text-[11.5px] text-ink">{domain}</span>
                  </div>
                ))}
              </div>
            )}
          </fieldset>

          {error && <p className="text-xs text-danger">{error}</p>}
        </form>
      )}
    </Modal>
  );
}
